import { AxiosError } from 'axios';
import i18n from '@/i18n';
import { del, edit, generateQueryString, get } from '@/utils';
import {
	IServerColumnConfig,
	IServerTableConfig
} from '@/components/table/column-config';
import {
	FEEDBACK_TYPE_PARAM,
	FeedbackStatus,
	FeedbackType,
	IFeedbackAsset,
	IFeedback,
	IFeedbackStatusInfo,
	IStatusLog
} from './types';

type IFeedbackResponse = {
	items?: unknown[];
	pageNumber?: number;
	totalPages?: number;
	totalCount?: number;
	ascending?: boolean;
	hasNextPage?: boolean;
	hasPreviousPage?: boolean;
	orderBy?: string | null;
};

type RawFeedback = Record<string, any>;

const normalizeStatus = (status: unknown): IFeedbackStatusInfo | FeedbackStatus => {
	if (!status) return '';

	if (typeof status === 'string') return status;

	const rawStatus = status as Record<string, any>;
	return {
		id: Number(rawStatus.id || 0),
		name: String(rawStatus.name || '')
	};
};

const normalizeStatusLog = (log: unknown): IStatusLog => {
	const rawLog = log as Record<string, any>;

	return {
		author: {
			id: Number(rawLog?.author?.id || rawLog?.performedBy?.id || 0),
			firstName: String(
				rawLog?.author?.firstName || rawLog?.performedBy?.firstName || ''
			),
			lastName: String(rawLog?.author?.lastName || rawLog?.performedBy?.lastName || ''),
			role: String(rawLog?.author?.role || ''),
			avatar: String(rawLog?.author?.avatar || '')
		},
		oldStatus: normalizeStatus(rawLog?.oldStatus),
		newStatus: normalizeStatus(rawLog?.newStatus),
		date: String(rawLog?.date || rawLog?.performedAt || '')
	};
};

const normalizeFeedback = (feedback: unknown): IFeedback => {
	const rawFeedback = feedback as RawFeedback;
	const assets = Array.isArray(rawFeedback.assets)
		? rawFeedback.assets.map(
				(asset: Record<string, any>): IFeedbackAsset => ({
					id: Number(asset.id || 0),
					fileName: String(asset.fileName || ''),
					originalName: String(asset.originalName || ''),
					mimeType: String(asset.mimeType || ''),
					extension: String(asset.extension || ''),
					sizeBytes: Number(asset.sizeBytes || 0),
					publicUrl: String(asset.publicUrl || ''),
					type: String(asset.type || ''),
					width:
						asset.width === undefined || asset.width === null
							? undefined
							: Number(asset.width),
					height:
						asset.height === undefined || asset.height === null
							? undefined
							: Number(asset.height),
					pageCount:
						asset.pageCount === undefined || asset.pageCount === null
							? undefined
							: Number(asset.pageCount)
				})
		  )
		: [];

	return {
		id: Number(rawFeedback.id || 0),
		user: {
			id: Number(rawFeedback?.user?.id || 0),
			firstName: String(rawFeedback?.user?.firstName || ''),
			lastName: String(rawFeedback?.user?.lastName || ''),
			email: rawFeedback?.user?.email ? String(rawFeedback.user.email) : undefined,
			countryCode: rawFeedback?.user?.countryCode
				? String(rawFeedback.user.countryCode)
				: undefined,
			phone: rawFeedback?.user?.phone ? String(rawFeedback.user.phone) : undefined
		},
		type: String(rawFeedback.type || ''),
		status: normalizeStatus(rawFeedback.status),
		description: String(rawFeedback.description || ''),
		deviceOs: rawFeedback.deviceOs ? String(rawFeedback.deviceOs) : null,
		deviceOsVersion: rawFeedback.deviceOsVersion
			? String(rawFeedback.deviceOsVersion)
			: null,
		deviceScreenResolution: rawFeedback.deviceScreenResolution
			? String(rawFeedback.deviceScreenResolution)
			: null,
		deviceBrand: rawFeedback.deviceBrand ? String(rawFeedback.deviceBrand) : null,
		deviceModel: rawFeedback.deviceModel ? String(rawFeedback.deviceModel) : null,
		deviceRamSize: rawFeedback.deviceRamSize ? String(rawFeedback.deviceRamSize) : null,
		deviceBatteryPercentage: rawFeedback.deviceBatteryPercentage
			? String(rawFeedback.deviceBatteryPercentage)
			: null,
		deviceReleaseHash: rawFeedback.deviceReleaseHash
			? String(rawFeedback.deviceReleaseHash)
			: null,
		assets,
		// DTO-ul de LISTĂ trimite doar numărul de imagini (`imageCount`), nu array-ul
		// `assets` (acela e pe detaliu) — îl păstrăm ca să afișăm corect coloana Imagine.
		imageCount:
			typeof rawFeedback.imageCount === 'number' ? rawFeedback.imageCount : undefined,
		image:
			rawFeedback.image
				? String(rawFeedback.image)
				: assets[0]?.publicUrl || undefined,
		deleted:
			typeof rawFeedback.deleted === 'boolean' ? rawFeedback.deleted : undefined,
		statusLogs: Array.isArray(rawFeedback.statusLogs)
			? rawFeedback.statusLogs.map(normalizeStatusLog)
			: [],
		createdAt: String(rawFeedback.createdAt || ''),
		updatedAt: rawFeedback.updatedAt ? String(rawFeedback.updatedAt) : undefined
	};
};

const appendPaginationParams = (query: string, page: number, items: number): string => {
	const separator = query.includes('?') ? '&' : '?';
	return `${query}${separator}PageNumber=${page + 1}&PageSize=${items}`;
};

async function fetchFeedback(
	type: FeedbackType,
	page: number,
	items: number,
	onResponse?: (page: number, items: number) => void
): Promise<IFeedback[]> {
	try {
		const query = generateQueryString({
			Type: FEEDBACK_TYPE_PARAM[type] ?? type,
			PageNumber: page + 1,
			PageSize: items
		});
		const res = await get(`api/feedbacks${query}`);
		const data = res.data as IFeedbackResponse;
		onResponse?.(data.totalPages || 1, data.totalCount || 0);
		return (data.items || []).map(normalizeFeedback);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('feedback.errorLoad'));
	}
}

async function fetchSingleFeedback(id: number): Promise<IFeedback> {
	try {
		const res = await get(`api/feedbacks/${id}`);
		return normalizeFeedback(res.data);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('feedback.errorLoad'));
	}
}
async function fetchFeedbackQuery(
	query: string,
	page: number,
	items: number,
	onResponse?: (page: number, items: number) => void
): Promise<IFeedback[]> {
	try {
		const res = await get(appendPaginationParams(`api/feedbacks${query}`, page, items));
		const data = res.data as IFeedbackResponse;
		onResponse?.(data.totalPages || 1, data.totalCount || 0);
		return (data.items || []).map(normalizeFeedback);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('feedback.errorLoad'));
	}
}

async function patchFeedback(
	id: number,
	statusId: number
): Promise<number | string> {
	try {
		const res = await edit(`api/feedbacks/${id}`, { statusId });
		return res?.status === 200 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('feedback.errorUpdate'));
	}
}

// GET api/feedbacks/table-config — configurația coloanelor tabelului de feedback
// (chei, etichete, ordine, vizibilitate/sortabilitate implicite). Se corelează în pagină
// cu definițiile locale prin applyServerColumnConfig.
async function fetchFeedbackTableConfig(): Promise<IServerColumnConfig[]> {
	try {
		const res = await get('api/feedbacks/table-config');
		const data = res.data as IServerTableConfig;
		return data?.columns ?? [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să încărcăm configurația tabelului.');
	}
}

async function deleteFeedback(feedbackId: number): Promise<string | number> {
	try {
		const res = await del(`api/feedbacks/${feedbackId}`);
		return res?.status === 200 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('feedback.errorDelete'));
	}
}

export {
	fetchFeedback,
	fetchFeedbackQuery,
	fetchFeedbackTableConfig,
	patchFeedback,
	deleteFeedback,
	fetchSingleFeedback
};
